import SimpleFormTemplateReader from './SimpleFormTemplateReader.js';
import SimpleFormDataMapper from './SimpleFormDataMapper.js';

/**
 * Main controller for simple form functionality
 * Handles form interactions, validation, and data processing
 */
export default class SimpleFormController {
    /**
     * Create a new simple form controller
     * 
     * @param {HTMLElement} form - Form element
     * @param {Object} options - Controller options
     */
    constructor(form, options = {}) {
        this.form = form;
        this.options = {
            templateId: window.stcTemplateId || 0,
            basePrice: parseFloat(window.stcBasePrice || 0),
            uploadEndpoint: '/wp-admin/admin-ajax.php',
            nonce: window.octStcCustomizer?.nonce || '',
            ...options
        };
        
        // Initialize components
        this.templateReader = new SimpleFormTemplateReader(this.options.templateId);
        this.dataMapper = new SimpleFormDataMapper(this.templateReader);
        
        // Form state
        this.isSubmitting = false;
        this.uploadedImage = null;
        this.validationErrors = {};
        this.editCartItemKey = null; // For update mode

        // Form elements
        this.elements = this.getFormElements();

        // Initialize
        this.initialize();
    }
    
    /**
     * Initialize the controller
     */
    async initialize() {
        try {
            // Load template configuration
            await this.templateReader.loadTemplate();
            
            // Setup form validation
            this.setupValidation();
            
            // Setup image upload
            this.setupImageUpload();
            
            // Setup form submission
            this.setupFormSubmission();
            
            // Update form state
            this.updateFormState();

            // Listen for edit mode from designer
            document.addEventListener('stc:simple-form:load-edit', (e) => {
                this.loadEditMode(e.detail.cartItemKey, e.detail.cartData);
            });

            console.log('Simple form controller initialized successfully');

        } catch (error) {
            console.error('Error initializing simple form controller:', error);
            // Continue with fallback configuration
            this.setupValidation();
            this.setupImageUpload();
            this.setupFormSubmission();
        }
    }
    
    /**
     * Get form elements
     * 
     * @returns {Object} Form elements object
     */
    getFormElements() {
        return {
            // Image elements
            imageInput: this.form.querySelector('#stc-user-image'),
            imageUploadArea: this.form.querySelector('#stc-image-upload-area'),
            imagePlaceholder: this.form.querySelector('.stc-image-upload-placeholder'),
            imagePreview: this.form.querySelector('#stc-image-preview'),
            previewImage: this.form.querySelector('#stc-preview-image'),
            removeImageBtn: this.form.querySelector('#stc-remove-image'),
            
            // Background removal elements
            backgroundRemovalField: this.form.querySelector('#stc-background-removal-field'),
            backgroundRemovalFormCheckbox: this.form.querySelector('#stc-background-removal-checkbox'),
            
            // Text inputs
            nameInput: this.form.querySelector('#stc-user-name'),
            teamNameInput: this.form.querySelector('#stc-team-name'),
            teamNumberInput: this.form.querySelector('#stc-team-number'),
            
            // Submit elements
            submitBtn: this.form.querySelector('#stc-simple-submit'),
            loadingIndicator: this.form.querySelector('#stc-simple-loading'),
            
            // Error elements
            imageError: this.form.querySelector('#stc-image-error'),
            nameError: this.form.querySelector('#stc-name-error'),
            teamNameError: this.form.querySelector('#stc-team-name-error'),
            teamNumberError: this.form.querySelector('#stc-team-number-error'),
            
            // Background removal popup elements
            backgroundRemovalPopup: document.querySelector('#stc-background-removal-popup'),
            backgroundRemovalClose: document.querySelector('#stc-background-removal-close'),
            backgroundRemovalAccept: document.querySelector('.stc-background-removal-accept'),
            backgroundRemovalDecline: document.querySelector('.stc-background-removal-decline')
        };
    }
    
    /**
     * Setup form validation
     */
    setupValidation() {
        const validationRules = this.templateReader.getValidationRules();
        
        // Real-time validation for text inputs
        if (this.elements.nameInput) {
            this.elements.nameInput.addEventListener('input', () => {
                this.validateField('user_name', this.elements.nameInput.value, validationRules.user_name);
                this.updateFormState();
            });
        }
        
        if (this.elements.teamNameInput) {
            this.elements.teamNameInput.addEventListener('input', () => {
                this.validateField('team_name', this.elements.teamNameInput.value, validationRules.team_name);
                this.updateFormState();
            });
        }
        
        if (this.elements.teamNumberInput) {
            this.elements.teamNumberInput.addEventListener('input', () => {
                this.validateField('team_number', this.elements.teamNumberInput.value, validationRules.team_number);
                this.updateFormState();
            });
        }
        
        // Background removal form checkbox listener
        if (this.elements.backgroundRemovalFormCheckbox) {
            this.elements.backgroundRemovalFormCheckbox.addEventListener('change', () => {
                this.handleBackgroundRemovalFormChange();
            });
        }
        
        // Setup background removal popup
        this.setupBackgroundRemovalPopup();
    }

    /**
     * Handle background removal form checkbox change
     */
    handleBackgroundRemovalFormChange() {
        const isChecked = this.elements.backgroundRemovalFormCheckbox ? 
            this.elements.backgroundRemovalFormCheckbox.checked : false;
        
        // Update stored preference
        if (this.uploadedImage) {
            this.uploadedImage.backgroundRemovalEnabled = isChecked;
        }
        
        console.log('Background removal preference changed to:', isChecked);
    }

    /**
     * Setup background removal popup functionality
     */
    setupBackgroundRemovalPopup() {
        if (!this.elements.backgroundRemovalPopup) return;
        
        // Close popup handlers
        if (this.elements.backgroundRemovalClose) {
            this.elements.backgroundRemovalClose.addEventListener('click', () => {
                this.declineBackgroundRemoval();
            });
        }
        
        if (this.elements.backgroundRemovalDecline) {
            this.elements.backgroundRemovalDecline.addEventListener('click', () => {
                this.declineBackgroundRemoval();
            });
        }
        
        if (this.elements.backgroundRemovalAccept) {
            this.elements.backgroundRemovalAccept.addEventListener('click', () => {
                this.acceptBackgroundRemoval();
            });
        }
        
        // Close on overlay click
        const overlay = this.elements.backgroundRemovalPopup.querySelector('.stc-background-removal-overlay');
        if (overlay) {
            overlay.addEventListener('click', () => {
                this.declineBackgroundRemoval();
            });
        }
        
        // Close on Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.elements.backgroundRemovalPopup.style.display !== 'none') {
                this.declineBackgroundRemoval();
            }
        });
    }

    /**
     * Show background removal popup
     */
    showBackgroundRemovalPopup() {
        if (!this.elements.backgroundRemovalPopup) return;
        
        this.elements.backgroundRemovalPopup.style.display = 'flex';
        document.body.style.overflow = 'hidden'; // Prevent background scrolling
        
        // Focus the accept button
        setTimeout(() => {
            if (this.elements.backgroundRemovalAccept) {
                this.elements.backgroundRemovalAccept.focus();
            }
        }, 100);
    }
    
    /**
     * Hide background removal popup
     */
    hideBackgroundRemovalPopup() {
        if (!this.elements.backgroundRemovalPopup) return;
        
        this.elements.backgroundRemovalPopup.style.display = 'none';
        document.body.style.overflow = ''; // Restore scrolling
    }

    /**
     * Accept background removal (enable by default)
     */
    acceptBackgroundRemoval() {
        console.log('User accepted background removal');
        
        // Set preference to enabled
        if (this.uploadedImage) {
            this.uploadedImage.backgroundRemovalEnabled = true;
        }
        
        // Update form checkbox
        if (this.elements.backgroundRemovalFormCheckbox) {
            this.elements.backgroundRemovalFormCheckbox.checked = true;
        }
        
        // Show the background removal field in the form
        this.showBackgroundRemovalField();
        
        // Hide popup
        this.hideBackgroundRemovalPopup();
        
        // Update form state
        this.updateFormState();
    }

    /**
     * Decline background removal (disable by default)
     */
    declineBackgroundRemoval() {
        console.log('User declined background removal');
        
        // Set preference to disabled
        if (this.uploadedImage) {
            this.uploadedImage.backgroundRemovalEnabled = false;
        }
        
        // Update form checkbox
        if (this.elements.backgroundRemovalFormCheckbox) {
            this.elements.backgroundRemovalFormCheckbox.checked = false;
        }
        
        // Show the background removal field in the form
        this.showBackgroundRemovalField();
        
        // Hide popup
        this.hideBackgroundRemovalPopup();
        
        // Update form state
        this.updateFormState();
    }

    /**
     * Show the background removal field in the form
     */
    showBackgroundRemovalField() {
        if (this.elements.backgroundRemovalField) {
            this.elements.backgroundRemovalField.style.display = 'block';
        }
    }
    
    /**
     * Hide the background removal field in the form
     */
    hideBackgroundRemovalField() {
        if (this.elements.backgroundRemovalField) {
            this.elements.backgroundRemovalField.style.display = 'none';
        }
    }
    
    /**
     * Confirm background removal choice and continue with image processing
     */
    confirmBackgroundRemovalChoice() {
        // Get the checkbox value
        const backgroundRemovalEnabled = this.elements.backgroundRemovalCheckbox ? 
            this.elements.backgroundRemovalCheckbox.checked : true;
        
        // Store the preference
        if (this.uploadedImage) {
            this.uploadedImage.backgroundRemovalEnabled = backgroundRemovalEnabled;
        }
        
        console.log('Background removal preference:', backgroundRemovalEnabled);
        
        // Hide popup
        this.hideBackgroundRemovalPopup();
        
        // Update form state
        this.updateFormState();
    }
    
    /**
     * Setup image upload functionality
     */
    setupImageUpload() {
        if (!this.elements.imageInput || !this.elements.imageUploadArea) return;
        
        // Click to upload
        this.elements.imageUploadArea.addEventListener('click', () => {
            this.elements.imageInput.click();
        });
        
        // File input change
        this.elements.imageInput.addEventListener('change', (e) => {
            this.handleImageUpload(e.target.files[0]);
        });
        
        // Remove image button
        if (this.elements.removeImageBtn) {
            this.elements.removeImageBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.removeImage();
            });
        }
        
        // Drag and drop support
        this.setupDragAndDrop();
    }
    
    /**
     * Setup drag and drop functionality
     */
    setupDragAndDrop() {
        const uploadArea = this.elements.imageUploadArea;
        if (!uploadArea) return;
        
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            uploadArea.addEventListener(eventName, this.preventDefaults, false);
        });
        
        ['dragenter', 'dragover'].forEach(eventName => {
            uploadArea.addEventListener(eventName, () => {
                uploadArea.classList.add('stc-drag-over');
            }, false);
        });
        
        ['dragleave', 'drop'].forEach(eventName => {
            uploadArea.addEventListener(eventName, () => {
                uploadArea.classList.remove('stc-drag-over');
            }, false);
        });
        
        uploadArea.addEventListener('drop', (e) => {
            const files = e.dataTransfer.files;
            if (files.length > 0) {
                this.handleImageUpload(files[0]);
            }
        }, false);
    }
    
    /**
     * Prevent default drag behaviors
     * 
     * @param {Event} e - Event object
     */
    preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
    }
    
    /**
     * Handle image upload
     * 
     * @param {File} file - Selected file
     */
    async handleImageUpload(file) {
        if (!file) return;
        
        try {
            // Validate file
            const validation = this.validateImageFile(file);
            if (!validation.isValid) {
                this.showFieldError('user_image', validation.error);
                return;
            }
            
            // Clear previous errors
            this.clearFieldError('user_image');
            
            // Create preview
            const dataUrl = await this.createImagePreview(file);
            
            // Store image file for later upload during form submission
            this.uploadedImage = {
                file: file,
                dataUrl: dataUrl,
                originalName: file.name,
                fileType: file.type,
                fileSize: file.size,
                needsProcessing: true,
                backgroundRemovalEnabled: true // Default to enabled
            };
            
            // Show preview
            this.showImagePreview(dataUrl);
            
            // Show background removal popup
            this.showBackgroundRemovalPopup();
            
            // Update form state
            this.updateFormState();
            
        } catch (error) {
            console.error('Error handling image upload:', error);
            this.showFieldError('user_image', 'Error processing image. Please try again.');
        }
    }
    
    /**
     * Validate image file
     * 
     * @param {File} file - File to validate
     * @returns {Object} Validation result
     */
    validateImageFile(file) {
        const rules = this.templateReader.getValidationRules().user_image;
        
        // Check file type
        if (!rules.accept.includes(file.type)) {
            return {
                isValid: false,
                error: rules.errorMessages.invalidType
            };
        }
        
        // Check file size
        if (file.size > rules.maxSize) {
            return {
                isValid: false,
                error: rules.errorMessages.maxSize
            };
        }
        
        return { isValid: true };
    }
    
    /**
     * Create image preview from file
     * 
     * @param {File} file - Image file
     * @returns {Promise<string>} Data URL
     */
    createImagePreview(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(e);
            
            reader.readAsDataURL(file);
        });
    }
    
    /**
     * Show image preview
     * 
     * @param {string} dataUrl - Image data URL
     */
    showImagePreview(dataUrl) {
        if (!this.elements.previewImage || !this.elements.imagePreview) return;
        
        this.elements.previewImage.src = dataUrl;
        this.elements.imagePreview.style.display = 'block';
        this.elements.imagePlaceholder.style.display = 'none';
    }
    
    /**
     * Remove uploaded image
     */
    removeImage() {
        // Clear stored image data
        this.uploadedImage = null;
        
        if (this.elements.imageInput) {
            this.elements.imageInput.value = '';
        }
        
        if (this.elements.imagePreview) {
            this.elements.imagePreview.style.display = 'none';
        }
        
        if (this.elements.imagePlaceholder) {
            this.elements.imagePlaceholder.style.display = 'flex';
        }
        
        this.clearFieldError('user_image');
        this.updateFormState();
    }
    
    /**
     * Setup form submission
     */
    setupFormSubmission() {
        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleFormSubmit();
        });
    }
    
    /**
     * Handle form submission
     */
    async handleFormSubmit() {
        if (this.isSubmitting) return;
        
        try {
            this.isSubmitting = true;
            this.showLoading(true);
            
            // Validate entire form
            const isValid = this.validateForm();
            if (!isValid) {
                throw new Error('Please fix the form errors before submitting');
            }
            
            // Extract form data
            const formData = this.extractFormData();

            // Handle image upload or use existing image
            let uploadedImageData = null;

            if (this.uploadedImage) {
                if (this.uploadedImage.file && !this.uploadedImage.existingImage) {
                    // New image uploaded - upload to server
                    console.log('📤 [SUBMIT] Uploading new image to server...');
                    uploadedImageData = await this.uploadImageToServer(this.uploadedImage.file);
                    console.log('✅ [SUBMIT] Image uploaded successfully:', uploadedImageData);
                } else if (this.uploadedImage.existingImage) {
                    // Existing image from edit mode - use existing data
                    console.log('♻️ [SUBMIT] Using existing image data');
                    uploadedImageData = {
                        id: this.uploadedImage.id,
                        url: this.uploadedImage.url,
                        backgroundRemovalEnabled: this.uploadedImage.backgroundRemovalEnabled
                    };
                    console.log('✅ [SUBMIT] Existing image data:', uploadedImageData);
                }
            }

            // Map to customization data structure
            const customizationData = this.dataMapper.mapToCustomizationData(formData, uploadedImageData);

            // Add image reference if present
            if (uploadedImageData) {
                const referenceId = `user_image_${Date.now()}`;
                this.dataMapper.addImageReference(customizationData, referenceId, uploadedImageData, 'user_image');
            }
            
            // Validate mapped data
            const validation = this.dataMapper.validateMappedData(customizationData);
            if (!validation.isValid) {
                throw new Error('Invalid form data: ' + validation.errors.join(', '));
            }
            
            // Submit to cart
            await this.submitToCart(customizationData);
            
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('Error: ' + error.message);
        } finally {
            this.isSubmitting = false;
            this.showLoading(false);
        }
    }

    /**
     * Upload image to server using existing image handler
     * 
     * @param {File} imageFile - Image file to upload
     * @returns {Promise<Object>} Upload result with ID and URL
     */
    async uploadImageToServer(imageFile) {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('action', 'octo_stc_upload_images');
            formData.append('nonce', this.options.nonce);
            formData.append('images[user_image]', imageFile);
            
            // Add reference metadata including background removal preference
            const referenceData = {
                stepId: 'image',
                side: 'front',
                fieldName: 'user_image',
                source: 'simple_form',
                backgroundRemovalEnabled: this.uploadedImage?.backgroundRemovalEnabled !== false
            };
            formData.append('reference', JSON.stringify(referenceData));
            
            // Create XMLHttpRequest for upload progress
            const xhr = new XMLHttpRequest();
            
            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    const percentComplete = (e.loaded / e.total) * 100;
                    console.log(`Upload progress: ${percentComplete.toFixed(1)}%`);
                    // You can add a progress bar here if desired
                }
            });
            
            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        if (response.success && response.data) {
                            const uploadData = response.data.user_image;
                            if (uploadData && uploadData.id) {
                                resolve({
                                    id: uploadData.id,
                                    url: uploadData.url,
                                    metadata: uploadData.metadata,
                                    originalName: imageFile.name,
                                    fileName: uploadData.metadata?.fileName || imageFile.name,
                                    fileType: imageFile.type,
                                    fileSize: imageFile.size,
                                    backgroundRemovalEnabled: this.uploadedImage?.backgroundRemovalEnabled !== false
                                });
                            } else {
                                reject(new Error('Upload successful but no image data received'));
                            }
                        } else {
                            reject(new Error(response.data || 'Upload failed'));
                        }
                    } catch (e) {
                        reject(new Error('Invalid response from server'));
                    }
                } else {
                    reject(new Error(`Upload failed with status: ${xhr.status}`));
                }
            });
            
            xhr.addEventListener('error', () => {
                reject(new Error('Network error during upload'));
            });
            
            xhr.open('POST', this.options.uploadEndpoint || '/wp-admin/admin-ajax.php');
            xhr.send(formData);
        });
    }
    
    /**
     * Extract form data
     * 
     * @returns {Object} Form data
     */
    extractFormData() {
        const formData = {};
        
        // Add image indicator if uploaded (file will be uploaded separately)
        if (this.uploadedImage) {
            formData.user_image = true; // Just a flag, actual file handled separately
            formData.background_removal_enabled = this.uploadedImage.backgroundRemovalEnabled !== false;
        }
        
        // Add text fields
        if (this.elements.nameInput?.value.trim()) {
            formData.user_name = this.elements.nameInput.value.trim();
        }
        
        if (this.elements.teamNameInput?.value.trim()) {
            formData.team_name = this.elements.teamNameInput.value.trim();
        }
        
        if (this.elements.teamNumberInput?.value.trim()) {
            formData.team_number = this.elements.teamNumberInput.value.trim();
        }
        
        return formData;
    }
    
    /**
     * Submit customization data to cart
     * 
     * @param {Object} customizationData - Prepared customization data
     */
    async submitToCart(customizationData) {
        // Check if we're updating an existing cart item
        if (this.editCartItemKey) {
            console.log('📝 [SIMPLE_FORM] Updating cart item:', this.editCartItemKey);
            await this.updateCartItem(customizationData);
        } else {
            console.log('📝 [SIMPLE_FORM] Adding new cart item');
            await this.addToCart(customizationData);
        }
    }

    /**
     * Add new item to cart
     *
     * @param {Object} customizationData - Customization data
     */
    async addToCart(customizationData) {
        // Find the hidden WooCommerce form
        const cartForm = document.querySelector('form.cart');
        if (!cartForm) {
            throw new Error('Cart form not found');
        }

        // Prepare clean data for cart
        const cartData = this.dataMapper.prepareForCart(customizationData);

        // Set customization data
        const customizationField = cartForm.querySelector('input[name="stc_customization"]');
        if (!customizationField) {
            throw new Error('Customization field not found');
        }

        customizationField.value = JSON.stringify(cartData);

        // Submit the form
        cartForm.submit();
    }

    /**
     * Update existing cart item
     *
     * @param {Object} customizationData - Customization data
     */
    async updateCartItem(customizationData) {
        // Prepare data for AJAX request
        const formData = new FormData();
        formData.append('action', 'octo_stc_update_cart_item');
        formData.append('nonce', this.options.nonce);
        formData.append('cart_item_key', this.editCartItemKey);

        // Prepare clean data for cart
        const cartData = this.dataMapper.prepareForCart(customizationData);
        formData.append('customization_data', JSON.stringify(cartData));

        // Send AJAX request
        const response = await fetch(window.octStcCustomizer.ajax_url, {
            method: 'POST',
            body: formData
        });

        const data = await response.json();

        if (!data.success) {
            throw new Error(data.data?.message || 'Failed to update cart item');
        }

        console.log('✅ [SIMPLE_FORM] Cart item updated successfully');

        // Redirect to cart
        window.location.href = '/cart/';
    }
    
    /**
     * Validate entire form
     * 
     * @returns {boolean} Whether form is valid
     */
    validateForm() {
        const validationRules = this.templateReader.getValidationRules();
        let isValid = true;

        // Clear previous errors
        this.validationErrors = {};

        // Validate image (accept both new uploads and existing images in edit mode)
        const hasImage = this.uploadedImage && (this.uploadedImage.file || this.uploadedImage.existingImage);
        if (!hasImage) {
            this.showFieldError('user_image', validationRules.user_image.errorMessages.required);
            isValid = false;
        }
        
        // Validate name
        const nameValue = this.elements.nameInput?.value.trim() || '';
        if (!this.validateField('user_name', nameValue, validationRules.user_name)) {
            isValid = false;
        }
        
        // Validate optional fields
        const teamNameValue = this.elements.teamNameInput?.value.trim() || '';
        if (teamNameValue && !this.validateField('team_name', teamNameValue, validationRules.team_name)) {
            isValid = false;
        }
        
        const teamNumberValue = this.elements.teamNumberInput?.value.trim() || '';
        if (teamNumberValue && !this.validateField('team_number', teamNumberValue, validationRules.team_number)) {
            isValid = false;
        }
        
        return isValid;
    }
    
    /**
     * Validate individual field
     * 
     * @param {string} fieldName - Field name
     * @param {string} value - Field value
     * @param {Object} rules - Validation rules
     * @returns {boolean} Whether field is valid
     */
    validateField(fieldName, value, rules) {
        // Check required
        if (rules.required && (!value || value.trim() === '')) {
            this.showFieldError(fieldName, rules.errorMessages.required);
            return false;
        }
        
        // Skip further validation if field is empty and not required
        if (!value || value.trim() === '') {
            this.clearFieldError(fieldName);
            return true;
        }
        
        // Check max length
        if (rules.maxLength && value.length > rules.maxLength) {
            this.showFieldError(fieldName, rules.errorMessages.maxLength);
            return false;
        }
        
        // Check pattern
        if (rules.pattern && !rules.pattern.test(value)) {
            this.showFieldError(fieldName, rules.errorMessages.pattern);
            return false;
        }
        
        this.clearFieldError(fieldName);
        return true;
    }
    
    /**
     * Show field error
     * 
     * @param {string} fieldName - Field name
     * @param {string} message - Error message
     */
    showFieldError(fieldName, message) {
        this.validationErrors[fieldName] = message;
        
        // Find error element
        const errorElement = this.form.querySelector(`#stc-${fieldName.replace('_', '-')}-error`);
        if (errorElement) {
            errorElement.textContent = message;
            errorElement.style.display = 'block';
        }
        
        // Add error class to field
        const fieldElement = this.form.querySelector(`#stc-${fieldName.replace('_', '-')}`);
        if (fieldElement) {
            fieldElement.closest('.stc-simple-field')?.classList.add('has-error');
        }
    }
    
    /**
     * Clear field error
     * 
     * @param {string} fieldName - Field name
     */
    clearFieldError(fieldName) {
        delete this.validationErrors[fieldName];
        
        // Find error element
        const errorElement = this.form.querySelector(`#stc-${fieldName.replace('_', '-')}-error`);
        if (errorElement) {
            errorElement.style.display = 'none';
        }
        
        // Remove error class from field
        const fieldElement = this.form.querySelector(`#stc-${fieldName.replace('_', '-')}`);
        if (fieldElement) {
            fieldElement.closest('.stc-simple-field')?.classList.remove('has-error');
        }
    }
    
    /**
     * Update form state (enable/disable submit button)
     */
    updateFormState() {
        if (!this.elements.submitBtn) return;

        // Check if we have an image (either new upload or existing from edit mode)
        const hasImage = !!(this.uploadedImage && (this.uploadedImage.file || this.uploadedImage.existingImage));
        const hasName = this.elements.nameInput?.value.trim().length > 0;
        const hasErrors = Object.keys(this.validationErrors).length > 0;

        const canSubmit = hasImage && hasName && !hasErrors && !this.isSubmitting;

        this.elements.submitBtn.disabled = !canSubmit;

        // Update button text based on state
        if (this.isSubmitting) {
            this.elements.submitBtn.textContent = 'Processing...';
        } else {
            // Show "Update Cart" in edit mode, "Add to Cart" otherwise
            this.elements.submitBtn.textContent = this.editCartItemKey ? 'Update Cart' : 'Add to Cart';
        }
    }
    
    /**
     * Show/hide loading state
     *
     * @param {boolean} show - Whether to show loading
     */
    showLoading(show) {
        if (!this.elements.loadingIndicator) return;

        this.elements.loadingIndicator.style.display = show ? 'flex' : 'none';

        if (this.elements.submitBtn) {
            this.elements.submitBtn.disabled = show;
        }
    }

    /**
     * Load simple form in edit mode from cart data
     *
     * @param {string} cartItemKey - Cart item key
     * @param {Object} cartData - Cart item data
     */
    async loadEditMode(cartItemKey, cartData) {
        console.log('📝 [SIMPLE_FORM_EDIT] ==========================================');
        console.log('📝 [SIMPLE_FORM_EDIT] Loading edit mode', cartItemKey);
        console.log('📝 [SIMPLE_FORM_EDIT] Cart data:', cartData);
        console.log('📝 [SIMPLE_FORM_EDIT] ==========================================');

        try {
            // Store cart item key for update
            this.editCartItemKey = cartItemKey;

            // Extract data from cart (now using original cart structure)
            const template = cartData.template;
            const sides = template.sides || {};
            const front = sides.front || {};
            const steps = front.steps || {};
            const imageReferences = template.imageReferences || {};

            console.log('📝 [SIMPLE_FORM_EDIT] Steps:', steps);
            console.log('📝 [SIMPLE_FORM_EDIT] Image references:', imageReferences);

            // Populate form fields

            // 1. User name (from 'name' step -> data.text)
            if (steps.name?.data?.text && this.elements.nameInput) {
                this.elements.nameInput.value = steps.name.data.text;
                console.log('✅ [SIMPLE_FORM_EDIT] Loaded name:', steps.name.data.text);
            } else {
                console.log('⚠️ [SIMPLE_FORM_EDIT] Name not found in steps.name.data.text');
            }

            // 2. Team name (from 'team_name' step -> data.text)
            if (steps.team_name?.data?.text && this.elements.teamNameInput) {
                this.elements.teamNameInput.value = steps.team_name.data.text;
                console.log('✅ [SIMPLE_FORM_EDIT] Loaded team name:', steps.team_name.data.text);
            } else {
                console.log('⚠️ [SIMPLE_FORM_EDIT] Team name not found in steps.team_name.data.text');
            }

            // 3. Team number (from 'number' step -> data.text)
            if (steps.number?.data?.text && this.elements.teamNumberInput) {
                this.elements.teamNumberInput.value = steps.number.data.text;
                console.log('✅ [SIMPLE_FORM_EDIT] Loaded team number:', steps.number.data.text);
            } else {
                console.log('⚠️ [SIMPLE_FORM_EDIT] Team number not found in steps.number.data.text');
            }

            // 4. User image (from 'image' step -> imageContent.imageSource)
            const imageStep = steps.image;
            console.log('📝 [SIMPLE_FORM_EDIT] Image step:', imageStep);

            if (imageStep?.imageContent?.imageSource) {
                const imageSource = imageStep.imageContent.imageSource;
                console.log('📝 [SIMPLE_FORM_EDIT] Image source:', imageSource);

                if (imageSource.url) {
                    // Create uploaded image object with existing image
                    this.uploadedImage = {
                        url: imageSource.url,
                        id: imageSource.id,
                        file: { name: 'existing-image' }, // Fake file to pass validation
                        existingImage: true, // Flag to indicate this is an existing image
                        backgroundRemovalEnabled: imageSource.metadata?.backgroundRemovalEnabled !== false
                    };

                    // Show preview
                    if (this.elements.previewImage) {
                        this.elements.previewImage.src = imageSource.url;
                        console.log('✅ [SIMPLE_FORM_EDIT] Set preview image src:', imageSource.url);
                    }
                    if (this.elements.imagePreview) {
                        this.elements.imagePreview.style.display = 'block';
                        console.log('✅ [SIMPLE_FORM_EDIT] Showing image preview');
                    }
                    if (this.elements.imagePlaceholder) {
                        this.elements.imagePlaceholder.style.display = 'none';
                        console.log('✅ [SIMPLE_FORM_EDIT] Hiding image placeholder');
                    }

                    // Update background removal checkbox
                    if (this.elements.backgroundRemovalFormCheckbox) {
                        this.elements.backgroundRemovalFormCheckbox.checked = this.uploadedImage.backgroundRemovalEnabled;
                        console.log('✅ [SIMPLE_FORM_EDIT] Set background removal:', this.uploadedImage.backgroundRemovalEnabled);
                    }

                    // Show background removal field
                    this.showBackgroundRemovalField();

                    console.log('✅ [SIMPLE_FORM_EDIT] Loaded image:', imageSource.url);
                }
            } else {
                console.log('⚠️ [SIMPLE_FORM_EDIT] Image not found in steps.image.imageContent.imageSource');
            }

            // Hide advanced design section in edit mode
            const advancedDesignSection = document.querySelector('.stc-advanced-design-section');
            if (advancedDesignSection) {
                advancedDesignSection.style.setProperty('display', 'none', 'important');
                console.log('✅ [SIMPLE_FORM_EDIT] Hidden advanced design section');
            } else {
                console.log('⚠️ [SIMPLE_FORM_EDIT] Advanced design section not found');
            }

            // Update form state
            this.updateFormState();

            // Show success message with summary
            console.log('✅ [SIMPLE_FORM_EDIT] ==========================================');
            console.log('✅ [SIMPLE_FORM_EDIT] Edit mode loaded successfully');
            console.log('✅ [SIMPLE_FORM_EDIT] ==========================================');
            console.log('📊 [SIMPLE_FORM_EDIT] Form populated with:');
            console.log('   • Name:', this.elements.nameInput?.value || '(empty)');
            console.log('   • Team Name:', this.elements.teamNameInput?.value || '(empty)');
            console.log('   • Team Number:', this.elements.teamNumberInput?.value || '(empty)');
            console.log('   • Image URL:', this.uploadedImage?.url || '(no image)');
            console.log('   • Image ID:', this.uploadedImage?.id || '(no id)');
            console.log('   • Background Removal:', this.uploadedImage?.backgroundRemovalEnabled || false);
            console.log('   • Edit Cart Item Key:', this.editCartItemKey);
            console.log('   • Submit Button Text:', this.elements.submitBtn?.textContent);
            console.log('   • Submit Button Enabled:', !this.elements.submitBtn?.disabled);
            console.log('✅ [SIMPLE_FORM_EDIT] ==========================================');

            // Show alert to user
            alert('Design loaded for editing. Make your changes and click "Update Cart" to save.');

        } catch (error) {
            console.error('❌ [SIMPLE_FORM_EDIT] Error loading edit mode:', error);
            alert('Error loading design for editing. Please try again.');
        }
    }
}